import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore, ArrowDropDownRounded } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import {
  StyledSearch,
  StyleChooseFilterButton,
  StyledLanguageButton,
  StyledSelectLanguageDropdown,
  StyledDropdownMenuItem,
} from './index.style';

const Search = ({
  search,
  value,
  name,
  title,
  options,
  open,
  onChangeOpen,
  onChange,
  isLanguageFilter = false,
  defaultLanguageFilter,
}) => {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguageFilter);
  const [languages, setLanguages] = useState(options);
  const [languageSearch, setLanguageSearch] = useState('');
  const [isUserInteracted, setIsUserInteracted] = useState(false);
  const dropdownRef = useRef(null);
  const menuItemRef = useRef(null);

  const handleChangeSearch = (item) => {
    if (isLanguageFilter) {
      setCurrentLanguage(item);
      setShowDropdown(false);
    }
    setIsUserInteracted(true);
    onChange({ ...search, [name]: item.value });
  };

  const handleSearchLanguage = (e) => {
    setLanguageSearch(e.target.value);
    const filteredLanguages = options.filter((item) =>
      item?.label?.toLowerCase().includes(e.target.value.trim().toLowerCase()),
    );
    setLanguages(filteredLanguages);
  };

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    if (!isUserInteracted) setCurrentLanguage(defaultLanguageFilter);
  }, [defaultLanguageFilter]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (value?.length === 0) {
      setCurrentLanguage(defaultLanguageFilter);
      setLanguageSearch('');
      setLanguages(options);
    }
  }, [value]);

  useEffect(() => {
    if (menuItemRef.current) {
      menuItemRef.current.scrollIntoView({
        block: 'start',
      });
    }
  }, [showDropdown]);

  return (
    <StyledSearch expanded={open} onChange={() => onChangeOpen(name)}>
      <AccordionSummary
        className="accordion-sumary"
        expandIcon={<ExpandMore className="icon-expandMore" />}
        classes={{ root: 'search-item-title', expanded: 'accordion-expanded' }}
      >
        <Typography className="accordion-name">
          {title ? t(title) : t(name)}
        </Typography>
      </AccordionSummary>
      {isLanguageFilter ? (
        <AccordionDetails
          className="search-language-item-content"
          ref={dropdownRef}
        >
          <StyledLanguageButton onClick={() => setShowDropdown(!showDropdown)}>
            {currentLanguage && (
              <img
                className="language-button-flag"
                src={currentLanguage?.roundImage}
                alt=""
              />
            )}

            <Typography className="language-button-text">
              {currentLanguage?.label}
            </Typography>
            <ArrowDropDownRounded
              sx={{
                fontSize: 24,
                fontWeight: 900,
                lineHeight: 24,
              }}
            />
          </StyledLanguageButton>
          {showDropdown && (
            <StyledSelectLanguageDropdown>
              <div className="search-language-bar">
                <SearchIcon sx={{ width: 24, height: 24 }} />
                <input
                  className="search-input"
                  type="text"
                  placeholder={t('newFindTitle')}
                  value={languageSearch}
                  onChange={handleSearchLanguage}
                />
              </div>
              <div className="dropdown-menu-items">
                {languages.map((item) => (
                  <StyledDropdownMenuItem
                    className="dropdown-menu-item"
                    role="button"
                    tabIndex="0"
                    key={item.value}
                    onClick={() => handleChangeSearch(item)}
                    isChoosed={item.value === currentLanguage?.value}
                    ref={
                      item.value === currentLanguage?.value ? menuItemRef : null
                    }
                  >
                    <img
                      className="dropdown-item-flag"
                      src={item.roundImage}
                      alt=""
                    />
                    <Typography className="dropdown-item-text">
                      {item.label}
                    </Typography>
                  </StyledDropdownMenuItem>
                ))}
              </div>
            </StyledSelectLanguageDropdown>
          )}
        </AccordionDetails>
      ) : (
        <AccordionDetails className="search-item-content">
          {options.map((item) => (
            <StyleChooseFilterButton
              key={item.label}
              onClick={() => handleChangeSearch(item)}
              value={item.value}
              isChoose={item.value === value}
            >
              <Typography className="button-name">{t(item.label)}</Typography>
            </StyleChooseFilterButton>
          ))}
        </AccordionDetails>
      )}
    </StyledSearch>
  );
};

export default Search;
